import type { SyntaxKey } from "@/model/tokens";

/**
 * Hand-tokenized code samples. `‹role:text›` marks a span; text outside markers is plain
 * editor text. Flags after the role: `/` search match, `*` current match, `@` matching
 * bracket, `~` error underline, `?` warning underline.
 */
const ROLE: Record<string, SyntaxKey> = {
  com: "comment",
  kw: "keyword",
  ctl: "control",
  fn: "function",
  me: "method",
  str: "string",
  esc: "escape",
  re: "regex",
  num: "number",
  const: "constant",
  bool: "boolean",
  ty: "type",
  bi: "builtin",
  var: "variable",
  par: "parameter",
  prop: "property",
  op: "operator",
  pun: "punctuation",
  tag: "tag",
  attr: "attribute",
  ns: "namespace",
  mac: "macro",
  h: "heading",
  link: "link",
};

export interface Span {
  text: string;
  role?: SyntaxKey;
  search?: "match" | "current";
  matchParen?: boolean;
  undercurl?: "error" | "warning";
}

export interface Line {
  spans: Span[];
  git?: "add" | "change" | "delete";
  diagnostic?: { level: "error" | "warning" | "info" | "hint"; message: string };
}

export interface Sample {
  id: string;
  label: string;
  file: string;
  lang: string;
  cursor: number;
  visual?: [number, number];
  popupAt?: number;
  lines: Line[];
}

function parseLine(src: string): Span[] {
  const spans: Span[] = [];
  const re = /‹([a-z]+)([/*@~?]*):([^›]*)›/g;
  let last = 0;
  for (const m of src.matchAll(re)) {
    if (m.index > last) spans.push({ text: src.slice(last, m.index) });
    const flags = m[2] ?? "";
    spans.push({
      text: m[3] ?? "",
      role: ROLE[m[1] ?? ""],
      search: flags.includes("*") ? "current" : flags.includes("/") ? "match" : undefined,
      matchParen: flags.includes("@"),
      undercurl: flags.includes("~") ? "error" : flags.includes("?") ? "warning" : undefined,
    });
    last = m.index + m[0].length;
  }
  if (last < src.length) spans.push({ text: src.slice(last) });
  return spans;
}

type Deco = Omit<Line, "spans">;

function sample(
  meta: Omit<Sample, "lines">,
  src: string,
  decos: Record<number, Deco> = {},
): Sample {
  const lines = src
    .replace(/^\n/, "")
    .split("\n")
    .map((l, i) => ({ spans: parseLine(l), ...decos[i] }));
  return { ...meta, lines };
}

export const LUA = sample(
  {
    id: "lua",
    label: "Lua",
    file: "lua/themes/init.lua",
    lang: "lua",
    cursor: 14,
    visual: [11, 12],
    popupAt: 14,
  },
  `
‹com:-- themes: load a palette and apply its highlight groups›
‹kw:local› ‹var:M› ‹op:=› ‹pun:{}›

‹com:---@param name string›
‹com:---@return table|nil›
‹kw:function› ‹var:M›‹pun:.›‹fn:load›‹pun@:(›‹par:name›‹pun@:)›
  ‹kw:local› ‹var~:ok›‹pun:,› ‹var/:palette› ‹op:=› ‹bi:pcall›‹pun:(›‹bi:require›‹pun:,› ‹str:"themes.palettes."› ‹op:..› ‹par:name›‹pun:)›
  ‹ctl:if› ‹kw:not› ‹var:ok› ‹ctl:then›
    ‹bi:vim›‹pun:.›‹me:notify›‹pun:((›‹str:"palette ›‹esc:%q›‹str: not found"›‹pun:):›‹me:format›‹pun:(›‹par:name›‹pun:),› ‹bi:vim›‹pun:.›‹prop:log›‹pun:.›‹prop:levels›‹pun:.›‹const:WARN›‹pun:)›
    ‹ctl:return› ‹const:nil›
  ‹ctl:end›
  ‹ctl:for› ‹var:group›‹pun:,› ‹var:spec› ‹kw:in› ‹bi:pairs›‹pun:(›‹var*:palette›‹pun:.›‹prop:groups›‹pun:)› ‹ctl:do›
    ‹var:spec›‹pun:.›‹prop:default› ‹op:=› ‹bool:true›
    ‹bi:vim›‹pun:.›‹prop:api›‹pun:.›‹me:nvim_set_hl›‹pun:(›‹num:0›‹pun:,› ‹var:group›‹pun:,› ‹var:spec›‹pun:)›
  ‹ctl:end›
  ‹ctl:return› ‹var/:palette›
‹kw:end›

‹ctl:return› ‹var:M›`,
  {
    6: { diagnostic: { level: "warning", message: "Unused local `ok`" }, git: "change" },
    9: { git: "add" },
    13: { git: "add" },
    14: { diagnostic: { level: "hint", message: "nvim_set_hl(ns_id, name, val)" } },
  },
);

export const TS = sample(
  {
    id: "ts",
    label: "TypeScript",
    file: "src/theme.ts",
    lang: "typescript",
    cursor: 9,
    visual: [5, 5],
  },
  `
‹kw:import› ‹pun:{› ‹var:readFile› ‹pun:}› ‹kw:from› ‹str:"node:fs/promises"›‹pun:;›
‹kw:import› ‹kw:type› ‹pun:{› ‹ty:Theme› ‹pun:}› ‹kw:from› ‹str:"./model"›‹pun:;›

‹com:/** Loads a theme and checks its background. */›
‹kw:export› ‹kw:async› ‹kw:function› ‹fn:loadTheme›‹pun@:(›‹par:path›‹pun::› ‹ty:string›‹pun:,› ‹par:min› ‹op:=› ‹num:4.5›‹pun@:)›‹pun::› ‹ty:Promise›‹pun:<›‹ty:Theme›‹pun:>› ‹pun:{›
  ‹kw:const› ‹var:raw› ‹op:=› ‹ctl:await› ‹fn:readFile›‹pun:(›‹par:path›‹pun:,› ‹str:"utf8"›‹pun:);›
  ‹kw:const› ‹var/:theme› ‹op:=› ‹ns:JSON›‹pun:.›‹me:parse›‹pun:(›‹var:raw›‹pun:)› ‹kw:as› ‹ty:Theme›‹pun:;›
  ‹ctl:if› ‹pun:(›‹op:!›‹re:/^#[0-9a-f]{6}$/›‹pun:.›‹me:test›‹pun:(›‹var*:theme›‹pun:.›‹prop:palette›‹pun:.›‹prop~:bg›‹pun:))› ‹pun:{›
    ‹ctl:throw› ‹kw:new› ‹ty:Error›‹pun:(›‹str:\`invalid background in ›‹pun:\${›‹par:path›‹pun:}›‹esc:\\n›‹str:\`›‹pun:);›
  ‹pun:}›
  ‹ctl:return› ‹pun:{› ‹op:...›‹var/:theme›‹pun:,› ‹prop:verified›‹pun::› ‹bool:true›‹pun:,› ‹prop:min› ‹pun:};›
‹pun:}›`,
  {
    7: {
      diagnostic: { level: "error", message: "Property 'bg' does not exist on type 'Palette'." },
      git: "change",
    },
    10: { git: "add" },
  },
);

export const PYTHON = sample(
  {
    id: "py",
    label: "Python",
    file: "themes/swatch.py",
    lang: "python",
    cursor: 10,
    visual: [7, 8],
  },
  `
‹kw:from› ‹ns:dataclasses› ‹kw:import› ‹fn:dataclass›
‹kw:import› ‹ns:re›

‹const:HEX› ‹op:=› ‹ns:re›‹pun:.›‹me:compile›‹pun:(›‹str:r"›‹re:^#[0-9a-f]{6}$›‹str:"›‹pun:)›

‹attr:@dataclass›‹pun:(›‹par:frozen›‹op:=›‹bool:True›‹pun:)›
‹kw:class› ‹ty:Swatch›‹pun::›
    ‹str:"""A named color."""›
    ‹prop:name›‹pun::› ‹ty:str›
    ‹prop:hex›‹pun::› ‹ty:str› ‹op:=› ‹str:"#000000"›

    ‹kw:def› ‹fn:luminance›‹pun@:(›‹bi:self›‹pun@:)› ‹op:->› ‹ty:float›‹pun::›
        ‹var:r›‹pun:,› ‹var:g›‹pun:,› ‹var:b› ‹op:=› ‹pun:(›‹bi:int›‹pun:(›‹bi:self›‹pun:.›‹prop:hex›‹pun:[›‹var:i› ‹pun::› ‹var:i› ‹op:+› ‹num:2›‹pun:],› ‹num:16›‹pun:)› ‹op:/› ‹num:255› ‹ctl:for› ‹var:i› ‹kw:in› ‹pun:(›‹num:1›‹pun:,› ‹num:3›‹pun:,› ‹num:5›‹pun:))›
        ‹ctl:return› ‹num:0.2126› ‹op:*› ‹var:r› ‹op:+› ‹num:0.7152› ‹op:*› ‹var:g› ‹op:+› ‹num:0.0722› ‹op:*› ‹var:b›

‹ctl:if› ‹bi:__name__› ‹op:==› ‹str:"__main__"›‹pun::›
    ‹bi:print›‹pun:(›‹fn:Swatch›‹pun:(›‹str:"blue"›‹pun:,› ‹str/:"#83a598"›‹pun:).›‹me:luminance›‹pun:(),› ‹const:None›‹pun:,› ‹bool:True›‹pun:)›`,
  {
    13: { git: "add" },
    17: { diagnostic: { level: "info", message: "Swatch is not subscriptable" } },
  },
);

export const RUST = sample(
  { id: "rs", label: "Rust", file: "src/palette.rs", lang: "rust", cursor: 9, visual: [3, 4] },
  `
‹kw:use› ‹ns:std›‹pun:::›‹ns:collections›‹pun:::›‹ty:HashMap›‹pun:;›

‹attr:#[derive(Debug, Clone)]›
‹kw:pub› ‹kw:struct› ‹ty:Palette›‹pun:<›‹ty:'a›‹pun:>› ‹pun:{›
    ‹prop:name›‹pun::› ‹op:&›‹ty:'a› ‹ty:str›‹pun:,›
    ‹prop:colors›‹pun::› ‹ty:HashMap›‹pun:<›‹ty:String›‹pun:,› ‹ty:u32›‹pun:>,›
‹pun:}›

‹kw:impl›‹pun:<›‹ty:'a›‹pun:>› ‹ty:Palette›‹pun:<›‹ty:'a›‹pun:>› ‹pun:{›
    ‹kw:pub› ‹kw:fn› ‹fn:get›‹pun@:(›‹op:&›‹bi:self›‹pun:,› ‹par:key›‹pun::› ‹op:&›‹ty:str›‹pun@:)› ‹op:->› ‹ty:Option›‹pun:<›‹ty:u32›‹pun:>› ‹pun:{›
        ‹bi:self›‹pun:.›‹prop:colors›‹pun:.›‹me:get›‹pun:(›‹par:key›‹pun:).›‹me:copied›‹pun:()›
    ‹pun:}›
‹pun:}›

‹kw:fn› ‹fn:main›‹pun:()› ‹pun:{›
    ‹kw:let› ‹kw:mut› ‹var/:p› ‹op:=› ‹ty:Palette› ‹pun:{› ‹prop:name›‹pun::› ‹str:"themes"›‹pun:,› ‹prop:colors›‹pun::› ‹ty:HashMap›‹pun:::›‹fn:new›‹pun:()› ‹pun:};›
    ‹var*:p›‹pun:.›‹prop:colors›‹pun:.›‹me:insert›‹pun:(›‹str:"blue"›‹pun:.›‹me:into›‹pun:(),› ‹num:0x83a598›‹pun:);›
    ‹mac:println!›‹pun:(›‹str:"›‹esc:{:?} {}›‹str:"›‹pun:,› ‹var:p›‹pun:.›‹me:get›‹pun:(›‹str:"blue"›‹pun:),› ‹var:p›‹pun:.›‹prop~:name›‹pun:);›
‹pun:}›`,
  {
    16: { git: "add" },
    17: { diagnostic: { level: "warning", message: "field `name` is never read" } },
  },
);

export const MARKDOWN = sample(
  { id: "md", label: "Markdown", file: "README.md", lang: "markdown", cursor: 2 },
  `
‹h:# themes›

Design ‹kw:**one**› palette and export it ‹link:[everywhere](https://themes.utimura.io)›.

‹h:## Apps›
‹pun:-› ‹pun:[x]› Ghostty, herdr, Neovim
‹pun:-› ‹pun:[ ]› WeeChat ‹com:(256 colors)›

‹com:> Colors are references, not copies.›

Run ‹str:\`pnpm dev\`› and open ‹link:http://localhost:5173›.`,
  { 6: { git: "add" } },
);

export const SAMPLES = [LUA, TS, PYTHON, RUST, MARKDOWN];
